import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { renderMentions, formatAttachmentsForDisplay } from "@/src/utils/chatUtils.js";
import DOMPurify from "dompurify";
import linkifyStr from "linkify-string";

// Re-export from chatUtils so consumers can import from a single module
export { formatAttachmentsForDisplay };

export const isSystemMessage = (message) => {
  return !!message?.is_system || !!message?._collapsedCount;
};

export const collapseConsecutiveSystemMessages = (sortedMessages) => {
  const result = [];
  let i = 0;
  while (i < sortedMessages.length) {
    const msg = sortedMessages[i];
    const sysType = msg?.is_system
      ? msg.text?.trim().startsWith("added ")
        ? "added"
        : msg.text?.trim().startsWith("removed ")
          ? "removed"
          : null
      : null;
    if (!sysType) {
      result.push(msg);
      i++;
      continue;
    }
    let j = i + 1;
    while (
      j < sortedMessages.length &&
      sortedMessages[j]?.is_system &&
      sortedMessages[j].user_id === msg.user_id
    ) {
      const nextType = sortedMessages[j].text?.trim().startsWith("added ")
        ? "added"
        : sortedMessages[j].text?.trim().startsWith("removed ")
          ? "removed"
          : null;
      if (nextType !== sysType) break;
      j++;
    }
    const groupSize = j - i;
    if (groupSize <= 2) {
      for (let k = i; k < j; k++) result.push(sortedMessages[k]);
    } else {
      const names = [];
      for (let k = i; k < j; k++) {
        const prefix = sysType === "added" ? "added " : "removed ";
        names.push(sortedMessages[k].text.trim().slice(prefix.length).trim());
      }
      const displayText =
        names.length <= 2
          ? `${sysType} ${names.join(" and ")}`
          : `${sysType} ${names[0]}, ${names[1]} and ${names.length - 2} other${names.length - 2 > 1 ? "s" : ""}`;
      result.push({ ...msg, text: displayText, _collapsedCount: groupSize });
    }
    i = j;
  }
  return result;
};

export const filterAndSortMessages = (messages) => {
  const sorted = (messages || [])
    .filter((m) => !m.thread_id || m.thread_id === m.id || m.broadcast_from_thread_id)
    .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
  return collapseConsecutiveSystemMessages(sorted);
};

/**
 * Check if a message should be visually grouped with the previous message
 * (same sender within 30 seconds).
 */
export const isGroupedMessage = (messages, index) => {
  if (index === 0) return false;
  const current = messages[index];
  const previous = messages[index - 1];
  if (!current || !previous) return false;
  if (!current.user_id || !previous.user_id) return false;
  if (String(current.user_id) !== String(previous.user_id)) return false;
  if (!current.sent_at || !previous.sent_at) return false;
  return Math.abs(new Date(current.sent_at) - new Date(previous.sent_at)) <= 30000;
};

export const shouldShowDateDivider = (messages, index) => {
  if (index === 0) return true;
  const current = messages[index];
  const previous = messages[index - 1];
  if (!current?.sent_at || !previous?.sent_at) return false;
  return new Date(current.sent_at).toDateString() !== new Date(previous.sent_at).toDateString();
};

export const getDateDividerLabel = (sentAt) => {
  if (!sentAt) return "";
  const date = new Date(sentAt);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return formatTimestamp(sentAt, "dateOnly");
};

export const getMessageSender = (message, allUsers = []) => {
  if (message?.sender) {
    return {
      name: message.sender.name || `${message.sender.first_name || ""} ${message.sender.last_name || ""}`.trim(),
      email: message.sender.email,
      profile_pic: message.sender.profile_pic,
      avatar_url: message.sender.avatar_url,
      avatar_color: message.sender.avatar_color,
    };
  }
  const user = allUsers.find((u) => String(u.id) === String(message?.user_id));
  if (user) {
    return {
      name: user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
      email: user.email,
      profile_pic: user.profile_pic,
      avatar_url: user.avatar_url,
      avatar_color: user.avatar_color,
    };
  }
  return { name: "Unknown User", email: "" };
};

export const getMessageSenderName = (message, allUsers = []) => {
  if (message?.sender) {
    return (
      message.sender.name ||
      `${message.sender.first_name || ""} ${message.sender.last_name || ""}`.trim() ||
      message.sender.email ||
      "Unknown User"
    );
  }
  const user = allUsers.find((u) => String(u.id) === String(message?.user_id));
  return user
    ? (user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "Unknown User")
    : "Unknown User";
};

export const renderMessageText = (msg) => {
  try {
    if (!msg?.text) return "";
    let text = msg.text;

    text = linkifyStr(text, {
      defaultProtocol: "https",
      target: "_blank",
      rel: "noopener noreferrer",
      className: "link_color no-underline hover:underline transition-all duration-300 ease-in-out",
      validate: { email: () => false },
    });

    const mentionedUsers = msg.mentioned_users || [];
    text = renderMentions(text, mentionedUsers);
    const brTag = String.fromCharCode(60) + "br" + String.fromCharCode(62);
    text = text.replace(/\n/g, brTag);

    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ["a", "br", "span", "mark"],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "data-user-id", "style"],
      ALLOWED_URI_REGEXP: /^https?:\/\//,
    });
  } catch (error) {
    console.error("Error rendering message with mentions:", error);
    return DOMPurify.sanitize(msg?.text || "");
  }
};

